import React, { useState, useEffect } from 'react';
import { API_LINK } from '../../apiLink/apiLink';
import Layout from '../../components/Layout';

const HodDashboard = ({ onNavigate, userInfo }) => {


  // API Data States
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);


  // Dashboard Data States
  const [dashboardStats, setDashboardStats] = useState({
    totalStudents: 0,
    totalDepartments: 0,
    facultyMembers: 0,
    placementRate: 0
  });

  // Growth indicators
  const [growthStats, setGrowthStats] = useState({
    studentsGrowth: 0,
    departmentsGrowth: 0,
    facultyGrowth: 0,
    placementGrowth: 0
  });

  // Dashboard Content States
  const [recentGrievances, setRecentGrievances] = useState([]);
  const [teacherAttendance, setTeacherAttendance] = useState([]);
  const [pendingReviews, setPendingReviews] = useState([]);
  const [supplyApplications, setSupplyApplications] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date());



  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);



  // Fetch Dashboard Data from API
  useEffect(() => {
    const fetchHodDashboard = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log('🔍 Starting API call to fetch HOD dashboard data...');

        // Get token from storage
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        console.log('🔑 Token found:', token ? 'Yes' : 'No');

        if (!token) {
          setError('Authentication token not found. Using fallback data.');
          setLoading(false);
          return;
        }

        // Construct the full API URL
        const apiUrl = `${API_LINK}/api/hod/dashboard`;
        console.log('🌐 API URL:', apiUrl);

        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        console.log('📡 Response status:', response.status);
        console.log('📡 Response ok:', response.ok);

        // Check if response is HTML (404 page) instead of JSON
        const contentType = response.headers.get('content-type');
        console.log('📄 Content-Type:', contentType);

        if (!response.ok) {
          if (response.status === 401) {
            console.log('🔐 Authentication failed');
            setError('Session expired. Please login again.');
            sessionStorage.clear();
            localStorage.clear();
            if (onNavigate) onNavigate('home');
            return;
          } else if (response.status === 404) {
            console.log('🔍 API endpoint not found');
            setError('API endpoint not found. Please check server configuration.');
            setLoading(false);
            return;
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        // Check if response is JSON
        if (!contentType || !contentType.includes('application/json')) {
          console.log('❌ Response is not JSON:', contentType);
          setError('Server returned non-JSON response. Please check API configuration.');
          setLoading(false);
          return;
        }

        const result = await response.json();
        console.log('📦 API Response:', result);

        if (result.success && result.data) {
          const data = result.data;
          console.log('✅ Processing API data...');



          // Update dashboard stats
          setDashboardStats({
            totalStudents: data.stats?.totalStudents || 0,
            totalDepartments: data.stats?.totalDepartments || 0,
            facultyMembers: data.stats?.facultyMembers || 0,
            placementRate: data.stats?.placementRate || 0
          });

          // Update growth stats
          setGrowthStats({
            studentsGrowth: data.growthStats?.studentsGrowth || 0,
            departmentsGrowth: data.growthStats?.departmentsGrowth || 0,
            facultyGrowth: data.growthStats?.facultyGrowth || 0,
            placementGrowth: data.growthStats?.placementGrowth || 0
          });

          // Update recent grievances
          setRecentGrievances(data.recentGrievances || []);

          // Update teacher attendance
          setTeacherAttendance(data.teacherAttendance || []);

          // Update pending reviews
          setPendingReviews(data.pendingReviews || []);

          // Update supply applications
          setSupplyApplications(data.supplyApplications || []);

          console.log('✅ HOD dashboard data updated successfully');

        } else {
          console.log('❌ API returned error:', result.message);
          setError(result.message || 'Failed to fetch dashboard data');
        }

      } catch (error) {
        console.error('💥 Error fetching HOD dashboard data:', error);

        // Handle specific errors
        if (error.name === 'SyntaxError' && error.message.includes('Unexpected token')) {
          setError('Server returned HTML instead of JSON. Please check if the API endpoint exists.');
        } else if (error.message.includes('fetch')) {
          setError('Cannot connect to server. Please ensure the server is running');
        } else {
          setError(`Failed to load dashboard data: ${error.message}`);
        }


      } finally {
        setLoading(false);
      }
    };

    // Add a small delay to ensure component is mounted
    const timer = setTimeout(fetchHodDashboard, 500);
    return () => clearTimeout(timer);
  }, [userInfo]);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <Layout role="hod" onNavigate={onNavigate} userInfo={userInfo} activePage="hod-dashboard">
      {({ hodData, isMobile, handleLogout }) => (
        <>
          {/* Loading Component */}
          {loading && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 'calc(100vh - 200px)',
              flexDirection: 'column',
              gap: '1rem'
            }}>
              <div style={{
                width: '50px',
                height: '50px',
                border: '4px solid #e3f2fd',
                borderTop: '4px solid #238548',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              <p style={{ color: '#666', fontSize: '1.1rem' }}>Loading HOD Dashboard...</p>
            </div>
          )}

          {/* Error Component */}
          {error && !loading && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 'calc(100vh - 200px)',
              flexDirection: 'column',
              gap: '1rem',
              padding: '2rem'
            }}>
              <div style={{
                backgroundColor: '#ffffff',
                padding: '3rem',
                borderRadius: '12px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                textAlign: 'center',
                maxWidth: '400px',
                border: '1px solid #e0e0e0'
              }}>
                <div style={{ fontSize: '4rem', color: '#ff6b6b', marginBottom: '1.5rem' }}>⚠️</div>
                <h3 style={{ color: '#333', margin: '0 0 1rem 0', fontSize: '1.4rem', fontWeight: 600 }}>Unable to Load HOD Dashboard</h3>
                <p style={{ color: '#666', margin: '0 0 2rem 0', fontSize: '1rem', lineHeight: 1.5 }}>{error}</p>
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                  <button onClick={() => window.location.reload()} style={{ backgroundColor: '#238548', color: 'white', border: 'none', padding: '0.75rem 1.5rem', borderRadius: '8px', cursor: 'pointer', fontSize: '1rem', fontWeight: 500 }}>Try Again</button>
                  <button onClick={handleLogout} style={{ backgroundColor: '#666', color: 'white', border: 'none', padding: '0.75rem 1.5rem', borderRadius: '8px', cursor: 'pointer', fontSize: '1rem', fontWeight: 500 }}>Logout</button>
                </div>
              </div>
            </div>
          )}

          {/* Main Content */}
          {!loading && !error && (
            <>
              {/* Welcome Section */}
              <section style={{
                background: '#ffffff',
                padding: '2rem',
                borderRadius: '12px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                marginBottom: '2rem',
                borderLeft: '4px solid #238548',
                width: '100%',
                boxSizing: 'border-box'
              }}>
                <h2 style={{
                  color: '#238548',
                  margin: '0 0 0.5rem 0',
                  fontSize: '1.8rem',
                  fontWeight: 'bold'
                }}>
                  Welcome, {hodData.name}!
                </h2>
                <p style={{
                  color: '#666',
                  margin: '0 0 0.5rem 0',
                  fontSize: '1rem'
                }}>
                  Department: {hodData.department}
                </p>
                <p style={{
                  color: '#666',
                  margin: 0,
                  fontSize: '1rem'
                }}>
                  Today's date: {currentTime.toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              </section>

              {/* Stats Cards */}
              <section style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: '1.5rem',
                marginBottom: '2rem',
                width: '100%',
                boxSizing: 'border-box'
              }}>
                {[
                  {
                    icon: '👥',
                    title: 'Total Students',
                    value: dashboardStats.totalStudents.toLocaleString(),
                    subtitle: growthStats.studentsGrowth > 0 ? `↑${growthStats.studentsGrowth}% from last semester` : 'Total enrolled students',
                    color: '#238548'
                  },
                  {
                    icon: '🏢',
                    title: 'Departments',
                    value: dashboardStats.totalDepartments,
                    subtitle: 'Active Departments',
                    color: '#238548'
                  },
                  {
                    icon: '👨‍🏫',
                    title: 'Faculty Members',
                    value: dashboardStats.facultyMembers,
                    subtitle: 'Across All Departments',
                    color: '#238548'
                  },
                  {
                    icon: '🎓',
                    title: 'Placement Rate',
                    value: `${dashboardStats.placementRate}%`,
                    subtitle: growthStats.placementGrowth > 0 ? `↑${growthStats.placementGrowth}% from last year` : 'Current placement rate',
                    color: '#238548'
                  }
                ].map((stat, index) => (
                  <div key={index} style={{
                    background: '#ffffff',
                    padding: '2rem',
                    borderRadius: '12px',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                    textAlign: 'center',
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    overflow: 'hidden',
                    width: '100%',
                    boxSizing: 'border-box',
                    minWidth: 0
                  }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-5px)';
                      e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.1)';
                    }}>
                    <div style={{
                      fontSize: '2.5rem',
                      color: stat.color,
                      marginBottom: '1rem'
                    }}>
                      {stat.icon}
                    </div>
                    <h3 style={{ margin: '0 0 0.5rem 0', color: '#333', fontSize: '1.1rem' }}>{stat.title}</h3>
                    <p style={{
                      fontSize: '2.2rem',
                      fontWeight: 'bold',
                      color: stat.color,
                      margin: '0.5rem 0'
                    }}>
                      {stat.value}
                    </p>
                    <p style={{
                      color: stat.subtitle.includes('↑') ? '#28a745' : '#666',
                      margin: 0,
                      fontSize: '0.9rem',
                      fontWeight: stat.subtitle.includes('↑') ? '500' : 'normal'
                    }}>
                      {stat.subtitle}
                    </p>
                  </div>
                ))}
              </section>

              {/* Quick Actions */}
              <section style={{
                background: '#ffffff',
                padding: '2rem',
                borderRadius: '12px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                marginBottom: '2rem',
                width: '100%',
                boxSizing: 'border-box'
              }}>
                <h3 style={{
                  color: '#333',
                  margin: '0 0 1.5rem 0',
                  fontSize: '1.4rem',
                  fontWeight: 'bold'
                }}>
                  Quick Actions
                </h3>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
                  gap: '1.5rem'
                }}>
                  {[
                    { icon: '👁️', label: 'View Grievances', path: 'hod-grievances' },
                    { icon: '👨‍🏫', label: 'Teacher Attendance', path: 'hod-teacher-attendance' },
                    { icon: '📝', label: 'Requisition', path: 'hod-requisition' },
                    { icon: '🔬', label: 'Lab Review', path: 'hod-lab-review' }
                  ].map((action, index) => {
                    return (
                      <div
                        key={index}
                        style={{
                          background: '#f8f9fa',
                          border: '2px solid #e9ecef',
                          borderRadius: '12px',
                          padding: '1.5rem',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          textAlign: 'center',
                          fontSize: '1rem',
                          fontWeight: '500',
                          color: '#333',
                          userSelect: 'none'
                        }}
                        onClick={() => onNavigate(action.path)}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#238548';
                          e.currentTarget.style.color = '#ffffff';
                          e.currentTarget.style.borderColor = '#238548';
                          e.currentTarget.style.transform = 'translateY(-3px)';
                          e.currentTarget.style.boxShadow = '0 6px 20px rgba(35, 133, 72, 0.3)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = '#f8f9fa';
                          e.currentTarget.style.color = '#333';
                          e.currentTarget.style.borderColor = '#e9ecef';
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                        onTouchStart={(e) => {
                          e.currentTarget.style.background = '#238548';
                          e.currentTarget.style.color = '#ffffff';
                          e.currentTarget.style.borderColor = '#238548';
                        }}
                        onTouchEnd={(e) => {
                          setTimeout(() => {
                            e.currentTarget.style.background = '#f8f9fa';
                            e.currentTarget.style.color = '#333';
                            e.currentTarget.style.borderColor = '#e9ecef';
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = 'none';
                          }, 150);
                        }}
                      >
                        <div style={{
                          fontSize: '2rem',
                          marginBottom: '0.5rem',
                          transition: 'inherit'
                        }}>
                          {action.icon}
                        </div>
                        <div style={{ transition: 'inherit' }}>
                          {action.label}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>

              {/* Recent Activities Grid */}
              <section style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                gap: '1.5rem',
                marginBottom: '2rem',
                width: '100%',
                boxSizing: 'border-box'
              }}>
                {/* Recent Grievances */}
                <div style={{
                  background: '#ffffff',
                  borderRadius: '12px',
                  boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                  overflow: 'hidden',
                  width: '100%',
                  boxSizing: 'border-box',
                  minWidth: 0
                }}>
                  <div style={{
                    background: '#238548',
                    color: '#ffffff',
                    padding: '1.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <span style={{ fontSize: '1.8rem' }}>👁️</span>
                      <h3 style={{ margin: 0, fontSize: '1.2rem' }}>Recent Grievances</h3>
                    </div>
                    <span style={{
                      background: 'rgba(255, 255, 255, 0.2)',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '20px',
                      fontSize: '0.8rem',
                      fontWeight: 'bold'
                    }}>
                      {recentGrievances.filter(g => g.status === 'pending').length} pending
                    </span>
                  </div>
                  <div style={{ padding: '1.5rem' }}>
                    {recentGrievances.length > 0 ? recentGrievances.slice(0, 3).map((grievance, index) => (
                      <div key={index} style={{
                        padding: '1rem',
                        borderBottom: index < 2 ? '1px solid #ecf0f1' : 'none',
                        transition: 'all 0.3s ease'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.5rem' }}>
                          <div>
                            <div style={{ fontWeight: 'bold', color: '#333', fontSize: '0.95rem' }}>{grievance.subject}</div>
                            <div style={{ color: '#666', fontSize: '0.85rem' }}>{grievance.studentName} • {grievance.department}</div>
                          </div>
                          <span style={{
                            padding: '0.25rem 0.75rem',
                            borderRadius: '15px',
                            fontSize: '0.7rem',
                            fontWeight: 'bold',
                            backgroundColor: grievance.status === 'pending' ? '#fff3cd' :
                              grievance.status === 'in-progress' ? '#d1ecf1' : '#d4edda',
                            color: grievance.status === 'pending' ? '#856404' :
                              grievance.status === 'in-progress' ? '#0c5460' : '#155724'
                          }}>
                            {grievance.status}
                          </span>
                        </div>
                        <p style={{ color: '#666', fontSize: '0.8rem', margin: '0.5rem 0 0 0' }}>
                          {grievance.description?.substring(0, 100)}...
                        </p>
                      </div>
                    )) : (
                      <p style={{ textAlign: 'center', color: '#666', padding: '2rem' }}>
                        No recent grievances
                      </p>
                    )}
                    <button
                      onClick={() => onNavigate('hod-grievances')}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        background: '#f8f9fa',
                        border: '1px solid #e9ecef',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        marginTop: '1rem',
                        transition: 'all 0.3s ease',
                        fontSize: '0.9rem',
                        fontWeight: '500',
                        color: '#333'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = '#238548';
                        e.target.style.color = '#ffffff';
                        e.target.style.borderColor = '#238548';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = '#f8f9fa';
                        e.target.style.color = '#333';
                        e.target.style.borderColor = '#e9ecef';
                      }}
                    >
                      View All Grievances
                    </button>
                  </div>
                </div>

                {/* Teacher Attendance */}
                <div style={{
                  background: '#ffffff',
                  borderRadius: '12px',
                  boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                  overflow: 'hidden',
                  width: '100%',
                  boxSizing: 'border-box',
                  minWidth: 0
                }}>
                  <div style={{
                    background: '#238548',
                    color: '#ffffff',
                    padding: '1.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <span style={{ fontSize: '1.8rem' }}>👨‍🏫</span>
                      <h3 style={{ margin: 0, fontSize: '1.2rem' }}>Today's Attendance</h3>
                    </div>
                    <span style={{
                      background: 'rgba(255, 255, 255, 0.2)',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '20px',
                      fontSize: '0.8rem',
                      fontWeight: 'bold'
                    }}>
                      {teacherAttendance.filter(t => t.status === 'Present').length}/{teacherAttendance.length}
                    </span>
                  </div>
                  <div style={{ padding: '1.5rem' }}>
                    {teacherAttendance.length > 0 ? teacherAttendance.slice(0, 5).map((teacher, index) => (
                      <div key={index} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '1rem',
                        borderBottom: index < Math.min(teacherAttendance.length - 1, 4) ? '1px solid #ecf0f1' : 'none',
                        transition: 'all 0.3s ease'
                      }}>
                        <div>
                          <div style={{ fontWeight: 'bold', color: '#333', fontSize: '0.95rem' }}>{teacher.name}</div>
                          <div style={{ color: '#666', fontSize: '0.85rem' }}>
                            {teacher.entryTime ? `Entry: ${teacher.entryTime}` : 'Not checked in'}
                          </div>
                        </div>
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          borderRadius: '15px',
                          fontSize: '0.8rem',
                          fontWeight: 'bold',
                          backgroundColor: teacher.status === 'Present' ? '#d4edda' : '#f8d7da',
                          color: teacher.status === 'Present' ? '#155724' : '#721c24'
                        }}>
                          {teacher.status}
                        </span>
                      </div>
                    )) : (
                      <p style={{ textAlign: 'center', color: '#666', padding: '2rem' }}>
                        No teacher attendance records
                      </p>
                    )}
                    <button
                      onClick={() => onNavigate('hod-teacher-attendance')}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        background: '#f8f9fa',
                        border: '1px solid #e9ecef',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        marginTop: '1rem',
                        transition: 'all 0.3s ease',
                        fontSize: '0.9rem',
                        fontWeight: '500',
                        color: '#333'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = '#238548';
                        e.target.style.color = '#ffffff';
                        e.target.style.borderColor = '#238548';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = '#f8f9fa';
                        e.target.style.color = '#333';
                        e.target.style.borderColor = '#e9ecef';
                      }}
                    >
                      View Detailed Attendance
                    </button>
                  </div>
                </div>
              </section>

              {/* Additional Management Sections */}
              <section style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                gap: '1.5rem',
                width: '100%',
                boxSizing: 'border-box'
              }}>
                {/* Pending Reviews */}
                <div style={{
                  background: '#ffffff',
                  borderRadius: '12px',
                  boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                  overflow: 'hidden',
                  width: '100%',
                  boxSizing: 'border-box',
                  minWidth: 0
                }}>
                  <div style={{
                    background: '#238548',
                    color: '#ffffff',
                    padding: '1.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem'
                  }}>
                    <span style={{ fontSize: '1.8rem' }}>📋</span>
                    <h3 style={{ margin: 0, fontSize: '1.2rem' }}>Pending Reviews</h3>
                  </div>
                  <div style={{ padding: '1.5rem' }}>
                    {pendingReviews.length > 0 ? pendingReviews.map((review, index) => (
                      <div key={index} style={{
                        padding: '1rem',
                        borderBottom: index < pendingReviews.length - 1 ? '1px solid #ecf0f1' : 'none',
                        transition: 'all 0.3s ease'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.5rem' }}>
                          <div>
                            <div style={{ fontWeight: 'bold', color: '#333', fontSize: '0.95rem' }}>{review.title}</div>
                            <div style={{ color: '#666', fontSize: '0.85rem' }}>{review.type} • {review.requestedBy}</div>
                          </div>
                          <span style={{
                            padding: '0.25rem 0.75rem',
                            borderRadius: '15px',
                            fontSize: '0.7rem',
                            fontWeight: 'bold',
                            backgroundColor: review.priority === 'High' ? '#f8d7da' : '#fff3cd',
                            color: review.priority === 'High' ? '#721c24' : '#856404'
                          }}>
                            {review.priority}
                          </span>
                        </div>
                        <p style={{ color: '#999', fontSize: '0.8rem', margin: 0 }}>
                          Due: {formatDate(review.dueDate)}
                        </p>
                      </div>
                    )) : (
                      <p style={{ textAlign: 'center', color: '#666', padding: '2rem' }}>
                        No pending reviews
                      </p>
                    )}
                    <button
                      onClick={() => onNavigate('hod-review-requests')}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        background: '#f8f9fa',
                        border: '1px solid #e9ecef',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        marginTop: '1rem',
                        transition: 'all 0.3s ease',
                        fontSize: '0.9rem',
                        fontWeight: '500',
                        color: '#333'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = '#238548';
                        e.target.style.color = '#ffffff';
                        e.target.style.borderColor = '#238548';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = '#f8f9fa';
                        e.target.style.color = '#333';
                        e.target.style.borderColor = '#e9ecef';
                      }}
                    >
                      View All Reviews
                    </button>
                  </div>
                </div>
              </section>

            </>
          )}
        </>
      )}
    </Layout>
  );
};

export default HodDashboard;